import { useState, useCallback, useMemo } from 'react';
import preferencesService, { type UserPreferences } from '../services/preferencesService';

// ============================================================================
// usePreferences Hook
// ============================================================================
//
// Provides reactive state for user preferences with automatic localStorage
// persistence. Each setter updates both React state and the storage layer.
//
// Usage:
//   const { preferences, setPreference, togglePreference, resetPreferences } = usePreferences({
//     darkMode: config.layout.defaultTheme === 'dark',
//     tabMode: config.layout.enableTabMode,
//   });
//
//   // Read
//   if (preferences.darkMode) { ... }
//
//   // Toggle
//   togglePreference('darkMode');
//
//   // Set explicitly
//   setPreference('tabMode', false);

export interface UsePreferencesReturn {
  preferences: UserPreferences;
  setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  togglePreference: (key: keyof UserPreferences) => void;
  resetPreferences: () => void;
}

export function usePreferences(
  configDefaults?: Partial<UserPreferences>
): UsePreferencesReturn {
  // Initialize from localStorage, falling back to config-based defaults
  const [preferences, setPreferences] = useState<UserPreferences>(() =>
    preferencesService.getAll(configDefaults)
  );

  // Set a single preference — updates state + persists
  const setPreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      preferencesService.set(key, value);
      setPreferences(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  // Toggle a boolean preference
  const togglePreference = useCallback(
    (key: keyof UserPreferences) => {
      setPreferences(prev => {
        const newValue = !prev[key];
        preferencesService.set(key, newValue);
        return { ...prev, [key]: newValue };
      });
    },
    []
  );

  // Reset all preferences to defaults
  const resetPreferences = useCallback(() => {
    preferencesService.reset();
    setPreferences(preferencesService.getAll(configDefaults));
  }, [configDefaults]);

  return useMemo(
    () => ({ preferences, setPreference, togglePreference, resetPreferences }),
    [preferences, setPreference, togglePreference, resetPreferences]
  );
}

export default usePreferences;
